import React, {
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import ReactSpeedometer, {
  CustomSegmentLabelPosition,
} from 'react-d3-speedometer';

import BrandingContext from '../../Context/BrandingContext';
import { Width } from '../../Context/WidthContext';

export default memo(FlexibleRangeGaugeWidget);

function FlexibleRangeGaugeWidget(props) {
  const [state, updateState] = useState({ height: 0, width: 0 });

  const { primary } = useContext(BrandingContext);
  const vw = useContext(Width);
  const containerRef = useRef();
  const {
    datapointID,
    key,
    ranges,
    secondGauge: { datapointID: datapointID1, key: key1, ranges: ranges1 } = {},
  } = props.widget?.settings || {};

  const lines = [
    { cID: datapointID, k: key, r: ranges },
    { cID: datapointID1, k: key1, r: ranges1 },
  ].reduce((acc, { cID, k, r }) => {
    if (!cID) return acc;

    const { signals = {} } = props.fetchedData?.get?.(cID)?.[0] || {};
    let { value } =
      Object.values(signals || {})?.find(({ name }) => name === k) || {};
    const { display_precision = 2, unit } =
      props.signalUnitsMap.get(cID)?.[k] || {};

    const displayValue = Number.isInteger(value)
      ? value
      : value?.toFixed?.(display_precision ?? 2) ?? '';

    const {
      customSegmentLabels,
      customSegmentStops,
      maxValue,
      minValue,
      segmentColors,
      segments,
    } = r.reduce(
      (acc, { color, label, max, min } = {}) => {
        acc.customSegmentLabels.push({
          color: '#494949',
          fontSize: datapointID1 ? '10px' : '14px',
          position:
            vw >= 768
              ? CustomSegmentLabelPosition.Inside
              : CustomSegmentLabelPosition.Outside,
          text: label,
        });
        acc.customSegmentStops = [
          ...new Set([...acc.customSegmentStops, min, max]),
        ].sort((a, b) => a - b);
        acc.segmentColors.push(color);
        acc.segments += 1;
        if (typeof acc.maxValue === 'undefined' || acc.maxValue < max) {
          acc.maxValue = max;
        }
        if (typeof acc.minValue === 'undefined' || acc.minValue > min) {
          acc.minValue = min;
        }

        return acc;
      },
      {
        customSegmentLabels: [],
        customSegmentStops: [],
        maxValue: undefined,
        minValue: undefined,
        segmentColors: [],
        segments: 0,
      }
    );

    let currentValueText = `${displayValue}${unit ? ` ${unit}` : ''}`;
    if (value < minValue) {
      currentValueText = `Current value: ${displayValue} < ${minValue}${
        unit ? ` ${unit}` : ''
      }`;
      value = minValue;
    }
    if (value > maxValue) {
      currentValueText = `Current value: ${displayValue} > ${maxValue}${
        unit ? ` ${unit}` : ''
      }`;
      value = maxValue;
    }

    acc.push({
      currentValueText,
      customSegmentLabels,
      customSegmentStops,
      maxValue,
      minValue,
      segmentColors,
      segments,
      signalName: k,
      value: typeof value === 'string' ? undefined : value,
    });
    return acc;
  }, []);

  const needleHeightRatio = useMemo(() => {
    switch (true) {
      case vw < 768 && lines.length === 2:
        return 0.6;
      case vw < 768 && lines.length === 1:
      case vw > 768 && lines.length === 2:
        return 0.75;
      default:
        return 0.9;
    }
  }, [lines.length, vw]);

  const ringWidth = useMemo(() => {
    switch (true) {
      case vw < 768 && lines.length === 2:
        return 15;
      case vw > 768 && lines.length === 2:
      case vw < 768 && lines.length === 1:
        return 30;
      default:
        return 60;
    }
  }, [lines.length, vw]);

  useEffect(() => {
    window.setTimeout(() => {
      const { width: containerWidth } =
        containerRef?.current?.getBoundingClientRect?.() || {};
      let width = containerWidth;
      if (lines.length === 2) {
        const padding = vw < 768 ? 10 : 0;
        width = containerWidth / 2 - padding;
      } else if (vw > 1500) {
        width = containerWidth * 0.75;
      } else if (vw < 992 && vw >= 576) {
        width = containerWidth * 0.5;
      }

      const height = width / 2 + 30;

      updateState((prevState) => ({ ...prevState, height, width }));
    });
  }, [lines.length, vw]);

  return (
    <div
      className="position-absolute h-100 w-100 row no-gutters align-items-stretch"
      ref={containerRef}
    >
      {lines.map((l, i) => {
        const noData = !l.value && l.value !== 0;
        return (
          <div className="col p-0 position-relative" key={`${l.cID}${i}`}>
            <div className="position-absolute h-100 w-100 d-flex flex-column align-items-center justify-content-center">
              {lines.length === 2 ? (
                <p className="text-center text-bold mb-0 px-1 px-md-3 three-quarter-text">
                  {noData ? '' : l.signalName}
                </p>
              ) : null}
              {noData ? (
                <div className="flex-fill d-flex align-items-center justify-content-center">
                  <p className="text-center three-quarter-text mb-0 mx-4 p-3 border border-hr-gray shadow-sm rounded rounded-lg">
                    {`No data for ${l.signalName}`}
                  </p>
                </div>
              ) : (
                <ReactSpeedometer
                  customSegmentLabels={l.customSegmentLabels}
                  currentValueText={l.currentValueText}
                  customSegmentStops={l.customSegmentStops}
                  forceRender
                  height={state.height}
                  maxValue={l.maxValue}
                  minValue={l.minValue}
                  needleColor={primary}
                  needleHeightRatio={needleHeightRatio}
                  needleTransitionDuration={0}
                  ringWidth={ringWidth}
                  segmentColors={l.segmentColors}
                  segments={l.segments}
                  textColor="#494949"
                  value={l.value}
                  valueTextFontSize="12px"
                  width={state.width}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

FlexibleRangeGaugeWidget.propTypes = {
  fetchedData: PropTypes.object,
  graphIndex: PropTypes.number,
  graphWidgetIDs: PropTypes.arrayOf(PropTypes.string),
  renderNextGraph: PropTypes.func,
  signalUnitsMap: PropTypes.object,
  widget: PropTypes.shape({
    _id: PropTypes.string,
    settings: PropTypes.shape({
      datapoint: PropTypes.object,
      datapointID: PropTypes.string,
      key: PropTypes.string,
      ranges: PropTypes.arrayOf(
        PropTypes.shape({
          color: PropTypes.string,
          label: PropTypes.string,
          max: PropTypes.number,
          min: PropTypes.number,
        })
      ),
      secondGauge: PropTypes.shape({
        datapoint: PropTypes.object,
        datapointID: PropTypes.string,
        key: PropTypes.string,
        ranges: PropTypes.arrayOf(
          PropTypes.shape({
            color: PropTypes.string,
            label: PropTypes.string,
            max: PropTypes.number,
            min: PropTypes.number,
          })
        ),
      }),
      title: PropTypes.string,
    }),
  }),
};
